<template>
  <div class="formulario-modelo">
    <div class="header-flex">
      <h1>Gestión de Rúbricas (Criterios de Evaluación)</h1>
    </div>
    <div class="divider"></div>

    <!-- Filtro por tipo de información -->
    <div class="filtro-section">
      <label>Filtrar por tipo de información</label>
      <div class="opciones-tipo">
        <button
          v-for="tipo in tiposInformacion"
          :key="tipo"
          class="btn opcion"
          :class="{ activo: filtroTipo === tipo }"
          @click="filtroTipo = tipo; cargarDatos()"
        >
          {{ tipo }}
        </button>
        <button
          class="btn opcion"
          :class="{ activo: filtroTipo === '' }"
          @click="filtroTipo = ''; cargarDatos()"
        >
          Todos
        </button>
      </div>
    </div>

    <!-- Tabla de criterios de evaluación existentes -->
    <div class="contenedor-tabla">
      <div v-if="loading" class="info">Cargando criterios de evaluación...</div>
      <table v-else-if="store.criteriosEvaluacion.length" class="lista-tabla">
        <thead>
          <tr>
            <th>ID</th>
            <th>Versión</th>
            <th>Tipo Información</th>
            <th>Tipo Componente</th>
            <th>Estructura</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ce in store.criteriosEvaluacion" :key="ce.id">
            <td>{{ ce.id }}</td>
            <td>{{ nombreVersion(ce.id_version) }}</td>
            <td><span class="badge">{{ ce.tipo_informacion }}</span></td>
            <td>{{ nombreTipoComponente(ce.id_tipo_componente) }}</td>
            <td>{{ resumenRubrica(ce) }}</td>
            <td class="acciones">
              <button class="btn btn-sm btn-ver" @click="abrirVisor(ce)">Ver</button>
              <button v-if="auth.tienePermiso('rubricas.editar')" class="btn btn-sm guardar" @click="editarCriterio(ce)">Editar</button>
              <button v-if="auth.tienePermiso('rubricas.eliminar')" class="btn btn-sm salir" @click="confirmarEliminar(ce.id!)">Eliminar</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="mensaje-vacio">No hay criterios de evaluación registrados.</p>
    </div>

    <!-- Escalas de valoración: nota → nivel de cumplimiento.
         Es OTRA cosa que las rúbricas de arriba, y por eso la escala 1–10 de
         los modelos históricos no aparecía en esta pantalla. Solo lectura: se
         siembran con el modelo, no se editan aquí. -->
    <div class="seccion-escalas">
      <button type="button" class="toggle-escalas" @click="escalasVisibles = !escalasVisibles">
        <span>Escalas de valoración ({{ escalasValoracion.length }})</span>
        <span class="toggle-icon">{{ escalasVisibles ? '▲' : '▼' }}</span>
      </button>

      <div v-if="escalasVisibles" class="escalas-contenido">
        <p class="escalas-ayuda">
          Traducen la calificación obtenida al nivel de cumplimiento. El límite
          inferior es inclusivo: en la escala 1–10, un 8.20 todavía es
          «alto grado» y un 8.21 ya es «plenamente».
        </p>

        <div v-if="cargandoEscalas" class="info">Cargando escalas de valoración...</div>

        <div v-for="esc in escalasValoracion" :key="esc.id_modelo ?? 'defecto'" class="escala-bloque">
          <div class="escala-header">
            <span class="escala-nombre">{{ esc.modelo ?? `Modelo ${esc.id_modelo}` }}</span>
            <span class="badge">escala {{ esc.grados.length ? Math.min(...esc.grados.map(g => Number(g.rango_min))) : 0 }}–{{ esc.escala_max }}</span>
          </div>
          <table class="lista-tabla">
            <thead>
              <tr>
                <th>Nivel de cumplimiento</th>
                <th>Desde</th>
                <th>Hasta</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="g in esc.grados" :key="g.id_grado ?? g.orden">
                <td>{{ g.nombre }}</td>
                <td>{{ Number(g.rango_min).toFixed(2) }}</td>
                <td>{{ Number(g.rango_max).toFixed(2) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-if="!cargandoEscalas && !escalasValoracion.length" class="mensaje-vacio">
          No hay escalas de valoración registradas.
        </p>
      </div>
    </div>

    <!-- Botón crear nuevo -->
    <div v-if="auth.tienePermiso('rubricas.crear')" class="botones" style="margin-bottom: 1.5rem">
      <button class="btn guardar" @click="abrirFormulario()">+ Nuevo Criterio de Evaluación</button>
    </div>

    <!-- Formulario crear/editar -->
    <div v-if="mostrarForm" class="formulario-crear">
      <h3>{{ editandoId ? 'Editar' : 'Nuevo' }} Criterio de Evaluación</h3>
      <div class="divider"></div>

      <!-- Errores del servidor -->
      <div v-if="Object.keys(erroresServidor).length" class="errores-servidor">
        <p v-for="(msgs, campo) in erroresServidor" :key="campo">
          <strong>{{ campo }}:</strong> {{ Array.isArray(msgs) ? msgs.join(', ') : msgs }}
        </p>
      </div>

      <div class="fila">
        <label>Versión del modelo (opcional)</label>
        <select v-model="idVersionForm" class="form-control">
          <option :value="null">— General (sin versión) —</option>
          <option v-for="v in versionesStore.versiones" :key="v.id_version" :value="v.id_version">
            {{ v.nombre }}
          </option>
        </select>
        <small v-if="idVersionForm" class="info-asignado">Escala máxima: {{ escalaMax }}</small>
      </div>

      <div class="fila">
        <label>Tipo de información *</label>
        <select v-model="form.tipo_informacion" class="form-control">
          <option value="" disabled>Seleccione...</option>
          <option v-for="t in tiposInformacion" :key="t" :value="t">{{ t }}</option>
        </select>
        <small v-if="form.id_tipo_componente" class="info-asignado">
          Componente asignado: {{ nombreTipoComponente(form.id_tipo_componente) }}
        </small>
      </div>

      <!-- C-01 (NC-01): editor POLIMÓRFICO — la UI se deriva del tipo de información -->

      <!-- documental → matriz [grado × criterio] de descriptores -->
      <div v-if="form.tipo_informacion === 'documental'" class="niveles-section">
        <div class="niveles-header">
          <h4>Matriz documental (criterios × grados)</h4>
          <div class="acciones">
            <button type="button" class="btn btn-sm guardar" @click="agregarCriterioDoc">+ Criterio</button>
            <button type="button" class="btn btn-sm guardar" @click="agregarGradoDoc">+ Grado</button>
          </div>
        </div>

        <div class="fila">
          <label>Criterios de la matriz (Coherencia, Claridad, Pertinencia, Difusión, Aplicación…)</label>
          <div v-for="(c, i) in formDocumental.criterios" :key="'crit-' + i" class="criterio-fila">
            <input v-model="formDocumental.criterios[i]" class="form-control" placeholder="Nombre del criterio" />
            <button type="button" class="btn btn-sm btn-eliminar" @click="eliminarCriterioDoc(i)">×</button>
          </div>
        </div>

        <div v-for="(grado, i) in formDocumental.grados" :key="'grado-' + i" class="nivel-card">
          <div class="nivel-header">
            <strong>Grado {{ i + 1 }}</strong>
            <button type="button" class="btn btn-sm btn-eliminar" @click="formDocumental.grados.splice(i, 1)">Eliminar</button>
          </div>
          <div class="nivel-grid">
            <div class="campo">
              <label>Grado *</label>
              <input v-model="grado.grado" class="form-control" placeholder="Se cumple plenamente" />
            </div>
            <div class="campo">
              <label>Banda (mín / máx) *</label>
              <div class="rango-doble">
                <input v-model.number="grado.rango_min" type="number" step="0.01" min="0" :max="escalaMax" class="form-control" />
                <input v-model.number="grado.rango_max" type="number" step="0.01" min="0" :max="escalaMax" class="form-control" />
              </div>
            </div>
            <div v-for="c in formDocumental.criterios" :key="c" class="campo campo-full">
              <label>Descriptor — {{ c || '(criterio sin nombre)' }} *</label>
              <input v-model="grado.descriptores[c]" class="form-control" placeholder="Descriptor cualitativo" />
            </div>
          </div>
        </div>

        <p v-if="!formDocumental.grados.length" class="mensaje-vacio">
          Agregue al menos un grado a la matriz documental.
        </p>
      </div>

      <!-- cuantitativa → rangos numéricos 0–5 con tendencia -->
      <div v-else-if="form.tipo_informacion === 'cuantitativa'" class="niveles-section">
        <div class="niveles-header">
          <h4>Niveles cuantitativos (rangos 0–{{ escalaMax }} con tendencia)</h4>
          <button type="button" class="btn btn-sm guardar" @click="agregarNivelCuant">+ Nivel</button>
        </div>

        <div v-for="(nivel, i) in formCuantitativa.niveles" :key="i" class="nivel-card">
          <div class="nivel-header">
            <strong>Nivel {{ i + 1 }}</strong>
            <button type="button" class="btn btn-sm btn-eliminar" @click="formCuantitativa.niveles.splice(i, 1)">Eliminar</button>
          </div>
          <div class="nivel-grid">
            <div class="campo">
              <label>Grado *</label>
              <input v-model="nivel.grado" class="form-control" placeholder="Se cumple plenamente" />
            </div>
            <div class="campo">
              <label>Tendencia</label>
              <input v-model="nivel.tendencia" class="form-control" placeholder="Creciente favorable" />
            </div>
            <div class="campo">
              <label>Rango mín *</label>
              <input v-model.number="nivel.rango_min" type="number" step="0.01" min="0" :max="escalaMax" class="form-control" />
            </div>
            <div class="campo">
              <label>Rango máx *</label>
              <input v-model.number="nivel.rango_max" type="number" step="0.01" min="0" :max="escalaMax" class="form-control" />
            </div>
            <div class="campo campo-full">
              <label>Descripción</label>
              <textarea v-model="nivel.descripcion" class="form-control" rows="2" placeholder="Descripción del nivel de cumplimiento…"></textarea>
            </div>
          </div>
        </div>

        <p v-if="!formCuantitativa.niveles.length" class="mensaje-vacio">
          Agregue al menos un nivel para la rúbrica.
        </p>
      </div>

      <!-- opinion → muestreo por grupo de interés + regla de conversión -->
      <div v-else-if="form.tipo_informacion === 'opinion'" class="niveles-section">
        <div class="niveles-header">
          <h4>Diseño de muestreo por grupo de interés</h4>
          <button type="button" class="btn btn-sm guardar" @click="agregarMuestra">+ Grupo</button>
        </div>

        <div v-for="(m, i) in formOpinion.muestreo" :key="i" class="nivel-card">
          <div class="nivel-header">
            <strong>Grupo {{ i + 1 }}</strong>
            <button type="button" class="btn btn-sm btn-eliminar" @click="formOpinion.muestreo.splice(i, 1)">Eliminar</button>
          </div>
          <div class="nivel-grid">
            <div class="campo">
              <label>Grupo de interés *</label>
              <input v-model="m.grupo_interes" class="form-control" placeholder="Estudiantes" />
            </div>
            <div class="campo">
              <label>Tipo de muestreo *</label>
              <input v-model="m.tipo_muestreo" class="form-control" placeholder="Aleatorio estratificado" />
            </div>
            <div class="campo">
              <label>Criterios de selección</label>
              <input v-model="m.criterios" class="form-control" placeholder="Matriculados 2026-1" />
            </div>
            <div class="campo">
              <label>Técnica *</label>
              <input v-model="m.tecnica" class="form-control" placeholder="Encuesta" />
            </div>
          </div>
        </div>

        <div class="nivel-card">
          <div class="nivel-header"><strong>Regla de conversión a 0–5</strong></div>
          <div class="nivel-grid">
            <div class="campo">
              <label>Fórmula *</label>
              <input v-model="formOpinion.conversion.formula" class="form-control" placeholder="puntaje_apreciado*100/puntaje_total" />
            </div>
            <div class="campo">
              <label>Escala *</label>
              <input v-model="formOpinion.conversion.escala" class="form-control" placeholder="porcentaje → 0-5" />
            </div>
          </div>
        </div>

        <!-- Tabla de valoración del MAAM: % de cumplimiento → banda 0–5 -->
        <div class="niveles-header">
          <h4>Tabla de valoración</h4>
          <button type="button" class="btn btn-sm guardar" @click="agregarGradoValoracion">+ Fila</button>
        </div>

        <div v-for="(g, i) in formOpinion.grados" :key="'g' + i" class="nivel-card">
          <div class="nivel-header">
            <strong>{{ g.grado || `Fila ${i + 1}` }}</strong>
            <button type="button" class="btn btn-sm btn-eliminar" @click="formOpinion.grados.splice(i, 1)">Eliminar</button>
          </div>
          <div class="nivel-grid">
            <div class="campo">
              <label>Grado de cumplimiento *</label>
              <input v-model="g.grado" class="form-control" placeholder="Se cumple plenamente" />
            </div>
            <div class="campo">
              <label>Grado de valoración</label>
              <input v-model="g.grado_valoracion" class="form-control" placeholder="Mejoramiento Continuo" />
            </div>
            <div class="campo">
              <label>Valoración</label>
              <input v-model="g.valoracion" class="form-control" placeholder="Plena o Completa" />
            </div>
            <div class="campo">
              <label>Escala cuantitativa (mín – máx) *</label>
              <div class="rango-fila">
                <input v-model.number="g.rango_min" type="number" step="0.01" min="0" :max="escalaMax" class="form-control" placeholder="4.5" />
                <span class="rango-sep">–</span>
                <input v-model.number="g.rango_max" type="number" step="0.01" min="0" :max="escalaMax" class="form-control" placeholder="5" />
              </div>
            </div>
            <div class="campo">
              <label>Cumplimiento % (mín – máx)</label>
              <div class="rango-fila">
                <input v-model.number="g.pct_min" type="number" step="1" min="0" max="100" class="form-control" placeholder="90" />
                <span class="rango-sep">–</span>
                <input v-model.number="g.pct_max" type="number" step="1" min="0" max="100" class="form-control" placeholder="100" />
              </div>
            </div>
            <div class="campo campo-ancho">
              <label>Juicio de valor</label>
              <textarea v-model="g.juicio_de_valor" class="form-control" rows="2" placeholder="Significa que…"></textarea>
            </div>
          </div>
        </div>

        <p v-if="!formOpinion.muestreo.length && !formOpinion.grados.length" class="mensaje-vacio">
          Agregue al menos un grupo de interés al muestreo o una fila a la tabla de valoración.
        </p>
      </div>

      <p v-else class="mensaje-vacio">
        Seleccione el tipo de información para configurar la rúbrica correspondiente.
      </p>

      <!-- Botones del formulario -->
      <div class="botones">
        <button class="btn salir" @click="cerrarFormulario">Cancelar</button>
        <button class="btn guardar" :disabled="guardando" @click="guardar">
          {{ guardando ? 'Guardando...' : (editandoId ? 'Actualizar' : 'Guardar') }}
        </button>
      </div>
    </div>

    <!-- Modal confirmación eliminar -->
    <MensajeConfirmacion
      :visible="showConfirmacion"
      title="Eliminar Criterio de Evaluación"
      message="¿Está seguro de que desea eliminar este criterio? Esta acción no se puede deshacer."
      confirmText="Eliminar"
      cancelText="Cancelar"
      @confirm="ejecutarEliminar"
      @cancel="showConfirmacion = false"
    />

    <!-- Visor de la rúbrica en el formato del instrumento -->
    <RubricaVisorModal
      :visible="visorVisible"
      :criterio="criterioEnVisor"
      :nombreTipoComponente="criterioEnVisor ? nombreTipoComponente(criterioEnVisor.id_tipo_componente) : ''"
      @cerrar="visorVisible = false"
    />

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * Definición de rúbricas (escalas de calificación).
 *
 * El formulario es POLIMÓRFICO: documental pide una matriz [grado × criterio],
 * cuantitativa pide niveles con rangos, y opinión pide la tabla de conversión.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/rubricas` (GestionRubricas) — permiso `rubricas.ver`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, reactive, onMounted, watch } from 'vue'
import { useGestionCriterioEvaluacion } from '@/store/GestionCriterioEvaluacion'
import { useGestionTipoComponente } from '@/store/GestionTipoComponente'
import { useGestionVersiones } from '@/store/GestionVersiones'
import useModelo from '@/store/GestionModelo'
import useAutenticar from '@/store/IngresoAutenticar'
import type {
  CriterioEvaluacion,
  RubricaCriterio,
  RubricaDocumental,
  RubricaCuantitativa,
  RubricaOpinion,
  MuestraOpinion,
  GradoValoracionOpinion,
  TipoInformacion,
  EscalaValoracion
} from '@/interfaces/CriterioEvaluacion'
import { getEscalasValoracionService } from '@/services/CriterioEvaluacionService'
import {
  esRubricaDocumental,
  esRubricaCuantitativa,
  esRubricaOpinion
} from '@/interfaces/CriterioEvaluacion'
import { validarRubrica } from '@/utils/validarRubrica'
import MensajeConfirmacion from '@/components/MensajeConfirmacion.vue'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import RubricaVisorModal from '@/components/RubricaVisorModal.vue'

const store = useGestionCriterioEvaluacion()
const auth = useAutenticar()
const tiposStore = useGestionTipoComponente()
const versionesStore = useGestionVersiones()
const modeloStore = useModelo()
const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')

const tiposInformacion = ['documental', 'cuantitativa', 'opinion']
const filtroTipo = ref('')
const loading = ref(false)
const mostrarForm = ref(false)
const guardando = ref(false)
const editandoId = ref<number | null>(null)
const showConfirmacion = ref(false)
const idEliminar = ref<number | null>(null)
// Visor de solo lectura de la rúbrica
const visorVisible = ref(false)
const criterioEnVisor = ref<CriterioEvaluacion | null>(null)
const erroresServidor = ref<Record<string, string[]>>({})
const idVersionForm = ref<number | null>(null)
const escalaMax = ref<number>(10)
// Escalas de valoración (tabla `grados`): nota → nivel de cumplimiento.
const escalasValoracion = ref<EscalaValoracion[]>([])
const escalasVisibles = ref(false)
const cargandoEscalas = ref(false)

watch(idVersionForm, async (val) => {
  if (!val) { escalaMax.value = 10; return }
  const version = await versionesStore.getVersionById(val)
  if (version?.id_modelo) {
    const modelo = await modeloStore.getModeloById(version.id_modelo)
    escalaMax.value = modelo?.escala_max ?? 10
  }
})

// C-01 (NC-01): un sub-formulario por tipo de información (rúbrica polimórfica)
const form = ref<{ tipo_informacion: TipoInformacion | ''; id_tipo_componente: number }>({
  tipo_informacion: '',
  id_tipo_componente: 0
})

const formDocumental = reactive<RubricaDocumental>({ criterios: [], grados: [] })
const formCuantitativa = reactive<RubricaCuantitativa>({ niveles: [] })
// En el formulario ambas listas SIEMPRE existen (simplifica la plantilla);
// al construir el payload se omite la que quede vacía, porque en la rúbrica
// muestreo y grados son opcionales por separado.
const formOpinion = reactive<{
  muestreo: MuestraOpinion[]
  grados: GradoValoracionOpinion[]
  conversion: { formula: string; escala: string }
}>({
  muestreo: [],
  grados: [],
  conversion: { formula: '', escala: '' }
})

// Al seleccionar tipo_informacion se resuelve automáticamente el id_tipo_componente.
// La normalización NFD elimina tildes para que "Información" y "Informacion" coincidan.
watch(() => form.value.tipo_informacion, (nuevoTipo) => {
  if (!nuevoTipo) {
    form.value.id_tipo_componente = 0
    return
  }
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
  const tipoEncontrado = tiposStore.tipos.find(t => {
    if (!t.nombre) return false
    const n = norm(t.nombre)
    if (nuevoTipo === 'documental')  return n.includes('documental') || n.includes('documento')
    if (nuevoTipo === 'cuantitativa') return n.includes('cuantitativ') || n.includes('estadistic')
    if (nuevoTipo === 'opinion')     return n.includes('opinion') || n.includes('encuesta')
    return false
  })
  form.value.id_tipo_componente = tipoEncontrado?.id_tipo_componente ?? 0
})

function limpiarSubformularios() {
  formDocumental.criterios = []
  formDocumental.grados = []
  formCuantitativa.niveles = []
  formOpinion.muestreo = []
  formOpinion.grados = []
  formOpinion.conversion = { formula: '', escala: '' }
}

function rubricaActual(): RubricaCriterio {
  switch (form.value.tipo_informacion) {
    case 'documental':
      return { criterios: [...formDocumental.criterios], grados: formDocumental.grados.map(g => ({ ...g, descriptores: { ...g.descriptores } })) }
    case 'opinion': {
      // Se omite la lista vacía: enviar muestreo:[] haría fallar la validación
      // de "al menos uno de muestreo o grados" por el lado equivocado.
      const opinion: RubricaOpinion = { conversion: { ...formOpinion.conversion } }
      if (formOpinion.muestreo.length) opinion.muestreo = formOpinion.muestreo.map(m => ({ ...m }))
      if (formOpinion.grados.length) opinion.grados = formOpinion.grados.map(g => ({ ...g }))
      return opinion
    }
    default:
      return { niveles: formCuantitativa.niveles.map(n => ({ ...n })) }
  }
}

/** Resumen legible de la estructura según el tipo (columna de la tabla). */
function resumenRubrica(ce: CriterioEvaluacion): string {
  const r = ce.criterio
  if (!r) return '—'
  if (esRubricaDocumental(r)) return `${r.criterios?.length ?? 0} criterios × ${r.grados?.length ?? 0} grados`
  if (esRubricaOpinion(r)) {
    const partes = []
    if (r.muestreo?.length) partes.push(`${r.muestreo.length} grupos de muestreo`)
    if (r.grados?.length) partes.push(`${r.grados.length} grados de valoración`)
    return partes.length ? partes.join(' · ') : '—'
  }
  if (esRubricaCuantitativa(r)) return `${r.niveles?.length ?? 0} niveles`
  return '—'
}

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error' = 'positivo') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

function nombreTipoComponente(id: number): string {
  return tiposStore.tipos.find(t => t.id_tipo_componente === id)?.nombre ?? `ID ${id}`
}

function nombreVersion(id: number | null | undefined): string {
  if (!id) return 'General'
  return versionesStore.versiones.find((v: any) => v.id_version === id)?.nombre ?? `Ver. ${id}`
}

async function cargarDatos() {
  loading.value = true
  try {
    await store.cargarTodos(filtroTipo.value || undefined)
  } finally {
    loading.value = false
  }
}

async function cargarEscalasValoracion() {
  cargandoEscalas.value = true
  try {
    escalasValoracion.value = await getEscalasValoracionService()
  } finally {
    cargandoEscalas.value = false
  }
}

function abrirFormulario() {
  editandoId.value = null
  form.value = { tipo_informacion: '', id_tipo_componente: 0 }
  idVersionForm.value = null
  escalaMax.value = 10
  limpiarSubformularios()
  erroresServidor.value = {}
  mostrarForm.value = true
}

function editarCriterio(ce: CriterioEvaluacion) {
  editandoId.value = ce.id ?? null
  form.value = {
    tipo_informacion: ce.tipo_informacion,
    id_tipo_componente: ce.id_tipo_componente
  }
  idVersionForm.value = ce.id_version ?? null
  limpiarSubformularios()

  const r = ce.criterio
  if (r && esRubricaDocumental(r)) {
    formDocumental.criterios = [...(r.criterios ?? [])]
    formDocumental.grados = (r.grados ?? []).map(g => ({ ...g, descriptores: { ...(g.descriptores ?? {}) } }))
  } else if (r && esRubricaOpinion(r)) {
    formOpinion.muestreo = (r.muestreo ?? []).map(m => ({ ...m }))
    formOpinion.grados = (r.grados ?? []).map(g => ({ ...g }))
    formOpinion.conversion = { ...(r.conversion ?? { formula: '', escala: '' }) }
  } else if (r && esRubricaCuantitativa(r)) {
    formCuantitativa.niveles = (r.niveles ?? []).map(n => ({ ...n }))
  }

  erroresServidor.value = {}
  mostrarForm.value = true
}

function cerrarFormulario() {
  mostrarForm.value = false
  editandoId.value = null
  erroresServidor.value = {}
}

function agregarCriterioDoc() {
  formDocumental.criterios.push('')
}

function eliminarCriterioDoc(i: number) {
  const nombre = formDocumental.criterios[i]
  formDocumental.criterios.splice(i, 1)
  formDocumental.grados.forEach(g => { delete g.descriptores[nombre] })
}

function agregarGradoDoc() {
  formDocumental.grados.push({ grado: '', rango_min: 0, rango_max: 0, descriptores: {} })
}

function agregarNivelCuant() {
  formCuantitativa.niveles.push({ grado: '', tendencia: '', rango_min: 0, rango_max: 0, descripcion: '' })
}

function agregarGradoValoracion() {
  formOpinion.grados.push({
    grado: '', grado_valoracion: '', valoracion: '',
    rango_min: 0, rango_max: 0, pct_min: null, pct_max: null, juicio_de_valor: ''
  })
}

function agregarMuestra() {
  formOpinion.muestreo.push({ grupo_interes: '', tipo_muestreo: '', criterios: '', tecnica: '' })
}

async function guardar() {
  erroresServidor.value = {}
  if (!form.value.id_tipo_componente) {
    mostrarMensaje('Seleccione el tipo de información para continuar.', 'error')
    return
  }
  const criterio = rubricaActual()
  const errorLocal = validarRubrica(form.value.tipo_informacion, criterio)
  if (errorLocal) {
    mostrarMensaje(errorLocal, 'error')
    return
  }

  guardando.value = true
  try {
    const payload = {
      tipo_informacion: form.value.tipo_informacion as TipoInformacion,
      id_tipo_componente: form.value.id_tipo_componente,
      criterio,
      id_version: idVersionForm.value ?? null
    }

    let result
    if (editandoId.value) {
      result = await store.actualizar(editandoId.value, payload)
    } else {
      result = await store.crear(payload)
    }

    if (result.ok) {
      mostrarMensaje(editandoId.value ? 'Criterio actualizado.' : 'Criterio creado exitosamente.')
      cerrarFormulario()
      await cargarDatos()
    } else {
      if (result.errors) erroresServidor.value = result.errors
      const firstError = result.errors ? Object.values(result.errors).flat()[0] : null
      mostrarMensaje(firstError || 'Error al guardar. Revise los campos.', 'error')
    }
  } finally {
    guardando.value = false
  }
}

function abrirVisor(ce: CriterioEvaluacion) {
  criterioEnVisor.value = ce
  visorVisible.value = true
}

function confirmarEliminar(id: number) {
  idEliminar.value = id
  showConfirmacion.value = true
}

async function ejecutarEliminar() {
  showConfirmacion.value = false
  if (!idEliminar.value) return
  const result = await store.eliminar(idEliminar.value)
  if (result.ok) {
    mostrarMensaje('Criterio eliminado.')
    await cargarDatos()
  } else {
    const firstError = result.errors ? Object.values(result.errors).flat()[0] : null
    mostrarMensaje(firstError || 'Error al eliminar.', 'error')
  }
  idEliminar.value = null
}

onMounted(async () => {
  await tiposStore.getTipos()
  await versionesStore.getVersiones()
  await cargarDatos()
  await cargarEscalasValoracion()
})
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
}

h1 {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
}

.divider {
  border: none;
  border-bottom: 1px solid #e0e0e0;
  margin: 12px 0 16px;
}

.filtro-section {
  margin-bottom: 1.5rem;
}

.filtro-section > label {
  font-weight: 600;
  display: block;
  margin-bottom: 8px;
}

.opciones-tipo {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.btn.opcion {
  background-color: #f9f9f9;
  border: 1px solid #ccc;
  color: #333;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  text-transform: capitalize;
}

.btn.opcion.activo {
  background-color: #0e69a0;
  color: white;
  border-color: #0e69a0;
}

.contenedor-tabla {
  width: 100%;
  overflow-x: auto;
  margin-bottom: 1.5rem;
}

.lista-tabla {
  width: 100%;
  border-collapse: collapse;
}

.lista-tabla th,
.lista-tabla td {
  padding: 10px 14px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
  font-size: 0.9rem;
}

.lista-tabla thead {
  background-color: #f5f5f5;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
}

.acciones {
  display: flex;
  gap: 0.5rem;
}

.badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  background-color: #e8f4fd;
  color: #0e69a0;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: capitalize;
}

.mensaje-vacio {
  color: #888;
  font-style: italic;
  padding: 1rem 0;
}

/* Escalas de valoración (solo lectura) */
.seccion-escalas {
  margin-bottom: 1.5rem;
}

.toggle-escalas {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f5f7fa;
  border: 1px solid #e0e0e0;
  border-radius: var(--radius-lg);
  font-weight: 600;
  color: #333;
  cursor: pointer;
}

.toggle-escalas:hover {
  background-color: #eef1f6;
}

.escalas-contenido {
  padding: 1rem 0 0;
}

.escalas-ayuda {
  color: #555;
  font-size: 0.85rem;
  margin-bottom: 1rem;
}

.escala-bloque {
  margin-bottom: 1.5rem;
  overflow-x: auto;
}

.escala-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.5rem;
}

.escala-nombre {
  font-weight: 600;
  color: #333;
}

.info {
  padding: 1rem 0;
  color: #555;
}

.formulario-crear {
  border: 1px solid #e0e0e0;
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  margin-top: 1rem;
  background-color: #fafafa;
}

.formulario-crear h3 {
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
}

.fila {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1rem;
}

.fila > label {
  font-weight: 600;
  font-size: 0.9rem;
}

.form-control {
  padding: 0.6rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-control:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.niveles-section {
  margin-top: 1.5rem;
}

.niveles-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.niveles-header h4 {
  margin: 0;
  font-size: 1rem;
}

.nivel-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  background: white;
}

.nivel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.nivel-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.campo {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.campo > label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #555;
}

.campo-full {
  grid-column: 1 / -1;
}

.campo-ancho {
  grid-column: 1 / -1;
}

.rango-fila {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.rango-fila .form-control {
  min-width: 0;
}

.rango-sep {
  color: #777;
  font-weight: 600;
}

.criterio-fila {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.4rem;
}

.criterio-fila .form-control {
  flex: 1;
}

.rango-doble {
  display: flex;
  gap: 0.5rem;
}

.errores-servidor {
  background-color: #fff0f0;
  border: 1px solid #fcc;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  color: #c00;
}

.errores-servidor p {
  margin: 0.25rem 0;
}

.botones {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

.btn {
  padding: 0.6rem 1.25rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: var(--font-family);
  font-size: 0.9rem;
}

.btn-sm {
  padding: 0.35rem 0.75rem;
  font-size: 0.8rem;
}

.btn.salir {
  background-color: white;
  border: 1px solid #ccc;
  color: #333;
}

.btn.salir:hover {
  background-color: #d41a1a;
  color: white;
}

.btn-ver {
  background-color: #0e69a0;
  color: white;
  border: none;
}

.btn-ver:hover {
  background-color: #0a5480;
}

.btn.guardar {
  background-color: #eea222;
  color: black;
}

.btn.guardar:hover {
  background-color: #0e69a0;
  color: white;
}

.btn.guardar:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-eliminar {
  background-color: #dc3545;
  color: white;
  border: none;
}

.btn-eliminar:hover {
  background-color: #b02a37;
}

.header-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.info-asignado {
  display: block;
  margin-top: 0.3rem;
  font-size: 0.8rem;
  color: #0e69a0;
  font-weight: 500;
}
</style>
